import { defineStore } from "pinia";
import { CARD_LIMIT, USER_TYPE, FUNCTION_OTHER_ID, YARN_STATUS } from '~/constants/common.js'
import moment from "moment"
import omit from 'lodash/omit'

export const useYarnStore = defineStore("yarn", () => {
  const searchStore = useSearchStore()
  const { locale } = useI18n()
  const langCommon = useLangCommon()
  const { getMaterialInitValue } = useMaterialInitValue()
  const listByCompany = ref([])
  const listByCompanyCount = ref(0)
  const list = ref([])
  const yarnStatusMap = ref([])
  const yarnStatus = ref(null)
  const listClickStatus = ref([])
  const yarnForm = ref(null)
  const colorTypeOptions = ref([])
  const handlingMarks = ref([])
  const yarnlogId = ref(null)
  const yarnNewArrivals = ref([])
  const yarnPopulars = ref([])
  const yarnDetail = ref(null)
  const yarnFormColors = ref([])
  const yarnColorLists = ref(null)
  const yarnRefImages = ref(null)
  const yarnColorLocalUrls = ref([])
  const searchCondition = ref(getSearchInitValue())
  const banYarnCard = ref(false)
  const yarnFormCopy = ref(null)

  const changeYarnFormColorCode = ({ index, code }) => {
    const target = yarnFormColors.value[index].colorCategoryIds
    if (target.includes(code)) {
      yarnFormColors.value[index].colorCategoryIds = target.filter(codeId => {
        return codeId !== code
      })
    } else {
      target.push(code)
    }
  }

  const changeYarnFormColorType = ({ index, type }) => {
    yarnFormColors.value[index].colorType = type
  }

  const replaceYarnFormColorImage = ({ index, base64 }) => {
    yarnFormColors.value[index].colorData = base64
  }

  const addYarnColorLocalUrls = (target) => {
    yarnColorLocalUrls.value.push(target)
  }

  const resetYarnColorLocalUrls = () => {
    yarnColorLocalUrls.value.forEach((blobUrl) => {
      window.URL.revokeObjectURL(blobUrl)
    })
    yarnColorLocalUrls.value = []
  }

  const getYarnStatus = async () => {
    const result = await usePost('/code/q/a', { mcdId: 21, locale: locale.value })
    const rows = result.data.data
    yarnStatus.value = rows.map((item) => {
      return { ...item }
    })
  }

  const getColorTypeOptions = async () => {
    const result = await usePost('/code/q/a', { mcdId: 18, locale: locale.value })
    const rows = result.data.data
    colorTypeOptions.value = rows.map(item => {
      return {
        ...item,
        text: item.codeName,
        value: item.codeId
      }
    })
  }
  
  const resetStoreCache = () => {
    yarnStatus.value = null
    yarnForm.value = null
  }
  
  const getDataForYarnPrSupplier = async () => {
    const [
      {
        data: { data: arrival },
      },
      {
        data: { data: popular },
      },
    ] = await Promise.all([
      usePost("/yarn/arrival/q/a", { locale: locale.value }),
      usePost("/yarn/popular/q/a", { locale: locale.value }),
    ]);
    yarnNewArrivals.value = arrival;
    yarnPopulars.value = popular;
  }

  const yarnStatusForRadio = () => {
    return yarnStatus.value && yarnStatus.value.length
    ? yarnStatus.value.reduce((previous, { codeId, codeName }) => {
        if (![21003, 21009].includes(codeId)) {
          previous.push({
            value: codeId,
            text: codeName
          })
        }
        return previous
      }, [])
    : []
  }
  
  const getYarnStatistic = ({
    yarnId,
    locale,
    fromDate = null,
    toDate = null,
    cancelToken,
  }) => {
    return new Promise((resolve) => {
      Promise.all([
        new Promise((resolve) => {
          usePost(
            "/download/q/t",
            {
              yarnId,
              fromDate,
              toDate,
              locale,
            }
          )
            .then(
              ({
                data: {
                  data: { totalCount = 0 },
                },
              }) => {
                resolve(totalCount);
              }
            )
            .catch(() => {
              nuxt.error({ statusCode: 403 });
            });
        }),
        new Promise((resolve) => {
          usePost(
            "/favorite/q/t",
            {
              yarnId,
              locale,
              fromDate,
              toDate,
            }
          )
            .then(
              ({
                data: {
                  data: { totalCount = 0 },
                },
              }) => {
                resolve(totalCount);
              }
            )
            .catch(() => {
              nuxt.error({ statusCode: 403 });
            });
        }),
        new Promise((resolve) => {
          usePost(
            "/viewed/q/t",
            {
              yarnId,
              locale,
              fromDate,
              toDate,
            }
          )
            .then(
              ({
                data: {
                  data: { totalCount = 0 },
                },
              }) => {
                resolve(totalCount);
              }
            )
            .catch(() => {
              nuxt.error({ statusCode: 403 });
            });
        }),
      ]).then((res) => {
        const [downloadedTime, favoriteTime, viewedTime] = res;
        resolve({
          downloadedTime,
          viewedTime,
          favoriteTime,
        });
      });
    });
  }

  const getDatamallRefImages = async ({ yarnId }) => {
    const result = await usePost('/yarn/dm/p/a', { yarnId })
    return result.data.data.data
  }

  const getYarnDetailFromServer = async ({ yarnlogId, pure, customLocale }) => {
    if (yarnDetail.value) {
      return yarnDetail.value
    }
    const { data } = await usePost('/yarn/q', {
      yarnlogId: Number(yarnlogId),
      locale: customLocale || locale.value
    })
    const detail = data.data
     if (Object.keys(detail).length === 0) {
      yarnDetail.value = { notExist: true }
      return
    }
    if (!pure) {
      if (detail.ecomarkId && detail.numLisence && detail.startEcoMark && detail.endEcoMark) {
        detail.ecomarkIds = detail.ecomarkId.split(',').map(id => {
          return Number(id)
        })
        detail.numCertification = detail.numLisence.split(',').map(numLisence =>{
          return numLisence
        })
        detail.startCertification = detail.startEcoMark.split(',').map(startEcoMark =>{
          return moment(startEcoMark).format('YYYY/MM/DD')
        })
        detail.endCertification = detail.endEcoMark.split(',').map(endEcoMark =>{
          return moment(endEcoMark).format('YYYY/MM/DD')
        })
        const certificationList = await searchStore.certificationForOption(searchStore.certifications)
        detail.certificationPaths = detail.ecomarkId.split(',').reduce((previous, id) => {
          previous.push(returnKeyOrFalsy(
            certificationList.find(({ codeId }) => {
            return codeId === Number(id)
          }
        ), 'imgPath', null))
          return previous
        }, []).filter(target => {
          return target
        })
      } else {
        detail.certificationPaths = []
        detail.ecomarkIds = []
        detail.numCertification = []
        detail.startCertification = []
        detail.endCertification = []
      }
      const { data } = await usePost('/yarn/hand/q/a', {
        yarnlogId: Number(yarnlogId),
        locale: customLocale || locale.value
      })
      const result = data.data
      yarnDetail.value = { handlingMarks: result.map(({ imgPath }) => {
        return `${imgPath}`
        }),
        ...detail
      }
    } else {
      yarnDetail.value = detail
    }
  }
  
  const get = async ({ id: companyId, publicYarn }) => {
    const result = await usePost(
      `/yarn/q/g`,
      {
        companyId,
        locale: locale.value
      }
    )
    if (result) {
      list.value = publicYarn
      ? result.data.data.filter(({ yarnStatus }) => {
          return yarnStatus === YARN_STATUS.PUBLIC
        })
      : result.data.data
    } else {
      yarnDetail.value = detail
    }
  }

  const getListByCompany = async ({ page = 1, limit = CARD_LIMIT, myYarn, ...rest }) => {
    const authStore = useAuthStore()
    let endpoint = authStore.user.userType === USER_TYPE.DEMO && myYarn ? '/yarn/q/g/s' : '/yarn/q/g'
    const params = {
      ...provideMappedSearch(searchCondition.value || getSearchInitValue()),
      page,
      limit,
      ...rest,
      locale: locale.value,
    }
    const result = await usePost(endpoint, params)
    const statusMap = result.data.data.reduce((final, each) => {
      return [...final, {
        yarnId: each.yarnId,
        yarnStatus: each.yarnStatus
      }]
    }, [])
    yarnStatusMap.value = statusMap
    const data = await usePost('/yarn/q/g/t', { ...rest, locale: locale.value })
    listByCompany.value = result.data.data
    listByCompanyCount.value = Number(data.data.data.TotalCount)
  }

  const deleteYarn = async ({ yarnId, yarnlogId }) => {
    const result = await usePost('/yarn/d', { yarnId, yarnlogId })
    return result.data.status
  }

  const getYarnColorListsFromServer = async ({ yarnlogId, customLocale }) => {
    const result = await usePost('/yarn/color/q', { yarnlogId, locale: customLocale || locale.value })
    yarnColorLists.value = result.data.data
  }

  const getYarnRefImagesFromServer = async ({ yarnlogId, customLocale }) => {
    const result = await usePost('/yarn/refimg/q/a', { yarnlogId, locale: customLocale || locale.value })
    yarnRefImages.value = result.data.data
  }

  const downloadYarn = async ({ yarnId, yarnlogId, userId, isDownloaded }) => {
    const { headers, data } = await usePost('/apex/down', { yarnId }, { responseType: 'blob' })
    const headersObject = Object.fromEntries(headers.entries())

    await usePost('/download/i', { yarnlogId, userId, isDownloaded })

    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/octet-stream' }))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', headersObject['content-disposition'].split('"')[1])
    link.setAttribute('type', 'application/octet-stream')
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    return true
  }

  const handleMarks = async () => {
    const result = await usePost('/code/q/a', { mcdId: 104, locale: locale.value })
    const rows = result.data.data
    handlingMarks.value = rows.map(({ codeName, codeId, imgPath }) => {
      return {
        text: codeName,
        value: codeId,
        imgPath
      }
    })
  }

  const getDataForYarnForm = async ({ yarnlogId, basicLanguageOptions }) => {
    const [
      {
        data: { data: yarn }
      },
      {
        data: { data: bookNames }
      },
      {
        data: { data: yarnpropergauges }
      },
      {
        data: { data: yarnMaterials }
      },
      {
        data: { data: handlingMarks }
      },
      {
        data: { data: yarncomments }
      },
      {
        data: { data: yarnColors }
      },
      {
        data: { data: referenceImages }
      },
      {
        data: { data: externalLinks }
      },
      {
        data: { data: funcI18ns }
      },
      {
        data: { data: ecos }
      },
      {
        data: { data: labels }
      },
      {
        data: { data: regions }
      }
    ] = await Promise.all([
      usePost('/yarn/q', { yarnlogId, locale: locale.value }),
      usePost('/yarn/book/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/pgauge/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/material/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/hand/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/comment/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/color/q', { yarnlogId, locale: locale.value }),
      usePost('/yarn/refimg/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/link/q/a', { yarnlogId }),
      usePost('/yarn/func/i18n/q/a', { yarnlogId, functionId: FUNCTION_OTHER_ID }),
      usePost('/yarn/eco/q/a', { yarnlogId }),
      usePost('/yarn/label/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/region/q/a', { yarnlogId, locale: locale.value })
    ])

    const externalLinksI18n = langCommon.languageOptions.value.reduce(
      (previous, { text, value }) => {
        doByCondition(
          externalLinks.find(({ locale }) => {
            return locale === value
          }),
          ({ locale, yarnLink }) => {
            previous.push({
              label: text,
              yarnlogId,
              locale,
              yarnLink
            })
          },
          () => {
            previous.push({
              label: text,
              locale: value,
              yarnLink: '',
              yarnlogId,
              creating: true
            })
          }
        )
        return previous
      },
      []
    )

    const i18nMaterials = await Promise.all(
      yarnMaterials.map(async ({ materialId, seq }) => {
        const {
          data: { data }
        } = await usePost('/yarn/material/i18n/q/a', {
          yarnlogId,
          materialId,
          seq
        })
  
        return {
          i18ns: langCommon.languageOptions.value.map(({ text, value }) => {
            const target = data.find(({ locale: materialLocale }) => {
              return materialLocale === value
            })
            return target
              ? {
                  seq,
                  locale: target.locale,
                  label: text,
                  displayName: target.displayName,
                  yarnlogId: target.yarnlogId,
                  materialId: target.materialId
                }
              : {
                  new: true,
                  locale: value,
                  label: text,
                  displayName: '',
                  yarnlogId,
                  materialId,
                  seq
                }
          }),
          materialId,
          seq
        }
      })
    )

    const materialwithI18n = yarnMaterials.map(
      ({ materialId, materialOption, materialDetailOption, seq, id, composition }) => {
        return {
          id,
          composition,
          materialId,
          materialOption,
          materialDetailOption,
          seq,
          i18nMaterials: i18nMaterials.find(
            ({ materialId: materialIdInI18n, seq: i18nSeq }) => {
              return materialIdInI18n === materialId && seq === i18nSeq
            }
          )
        }
      }
    )

    const referenceImagesDefault = referenceImages.findIndex(
      ({ defaultImage }) => {
        return defaultImage === 'Y'
      }
    )

    const colorDefaultIndex = yarnColors.findIndex(({ defaultImage }) => {
      return defaultImage === 'Y'
    })

    yarnForm.value = {
      ...yarn,
      labels:
        labels && labels?.length
          ? labels.map(({ labelId }) => {
              return labelId
            })
          : [],
      referDeletingImages: [],
      externalLinksI18n,
      characteristicId:
        typeof yarn.characteristicId === 'string'
          ? yarn.characteristicId
              .split(',')
              .filter(n => n)
              .map(Number)
          : [],
      functionId:
        typeof yarn.functionId === 'string'
          ? yarn.functionId
              .split(',')
              .filter(n => n)
              .map(Number)
          : [],
      thumbnailName: null,
      thumbnailData: null,
      bookNames: langCommon.languageOptions.value.map(({ value, text }) => {
        const target = bookNames.find(({ locale }) => {
          return locale === value
        })
        return {
          locale: value,
          bookName: target ? target.bookName : '',
          label: text
        }
      }),
      properGaugeList: yarnpropergauges.map(yarnpropergauge => {
        // 값
        return {
          gauge: yarnpropergauge.properGaugeId,
          properGauge: yarnpropergauge.properGaugeName,
          gaugeEnds: yarnpropergauge.ends
        }
      }),
      materials:
        materialwithI18n.length === 0
          ? getMaterialInitValue(langCommon.languageOptions.value)
          : materialwithI18n,
      freeComments: langCommon.languageOptions.value.map(({ value, text }) => {
        const result = doByCondition(
          yarncomments.find(({ locale }) => {
            return locale === value
          }),
          ({ freeComment }) => {
            return {
              locale: value,
              label: text,
              freeComment
            }
          },
          () => {
            return { locale: value, freeComment: '', label: text }
          }
        )
        return result.true || result.false
      }),
      colorDefaultIndex: colorDefaultIndex === -1 ? null : colorDefaultIndex,
      colors: yarnColors.map(
        ({
          colorName,
          colorType,
          colorCategoryId,
          imgPath,
          id,
          seq,
          defaultImage
        }) => {
          const col = {
            colorName,
            colorType,
            imgPath,
            id,
            seq
          }
          col.colorData = null
          col.colorCategoryIds = colorCategoryId
            ? colorCategoryId.split(',').map(Number)
            : []
          return col
        }
      ),
      referenceImagesDefault:
        referenceImagesDefault === -1 ? null : referenceImagesDefault,
      referenceImages: referenceImages.map(referenceImage => {
        const el = { ...referenceImage }
        el.defaultImage = !el.defaultImage ? 'N' : el.defaultImage
        el.imgName = ''
        el.imgData = ''
        return el
      }),
      handlingMarks: handlingMarks.map(({ handlingMarkId }) => {
        return handlingMarkId
      }),
      certificationId: ecos.map(({ ecoMarkId }) => {
        return ecoMarkId
      }),
      numCertification: ecos.map(({ numLisence }) => {
        return numLisence
      }),
      startCertification: ecos.map(({ startEcoMark }) => {
        return startEcoMark
      }),
      endCertification: ecos.map(({ endEcoMark }) => {
        return endEcoMark
      }),
      funcI18ns: langCommon.languageOptions.value.map(({ text, value }) => {
        const target = funcI18ns.find(({ locale }) => {
          return locale === value
        })
        return target
          ? {
              locale: target.locale,
              label: text,
              displayName: target.displayName
            }
          : {
              locale: value,
              label: text,
              displayName: ''
            }
      }),
      regions: langCommon.languageOptions.value.map(({ text, value }) => {
        const target = regions.find(({ locale }) => {
          return locale === value
        })
        return target
          ? {
              yarnlogId,
              locale: target.locale,
              label: text,
              regionName: target.regionName,
            }
          : {
              yarnlogId,
              locale: value,
              label: text,
              regionName: '',
            }
      })
    }
  }

  const updateWholeYarn = async ({
    referenceImagesDefault,
    colorDefaultIndex,
    functionId,
    skipNotification,
    deletingMaterial,
    yarnlogId,
    bookNames,
    freeComments,
    materials,
    characteristicId,
    externalLinksI18n,
    locale,
    referenceImages,
    handlingMarks,
    certificationId,
    numCertification,
    startCertification,
    endCertification,
    referDeletingImages,
    colors,
    apexColorCategory,
    apexColorType,
    funcI18ns,
    labels,
    properGaugeList,
    certificationList,
    isCopy,
    insertRefImage,
    ...rest
   }) => {
    if (!yarnlogId) {
      throw new Error('yarnlogId required')
    }
    return apiWrapper(async () => {
      await useBaseStore().getUserMe()

      // delete previous one
      await usePost('/yarn/book/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/char/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/func/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/comment/d/a', { yarnlogId, skipNotification })
      const ecoOld = await usePost('/yarn/eco/q/a', { yarnlogId, skipNotification })
      await usePost('/yarn/eco/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/hand/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/pgauge/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/func/i18n/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/label/d/a', { yarnlogId, skipNotification })
      await usePost('/yarn/region/d/a', { yarnlogId, skipNotification })
      await usePost('/apex/upload/color', {
        skipNotification,
        yarnId: rest.yarnId,
        firstDeleteAll: false,
        colors: colors.map(
          ({ colorCategoryIds, colorType, colorData, ...rest }, index) => {
            const target = {
              ...rest,
              defaultImage: index === colorDefaultIndex ? 'Y' : 'N',
              colorData: colorData ? colorData.split(',')[1] : null,
              colorType: apexColorType[colorType],
              colorCategories: colorCategoryIds.map(colorCategory => {
                return {
                  colorCategory: apexColorCategory[colorCategory]
                }
              })
            }
            if (!target.colorData) {
              delete target.colorData
            }
            delete target.imgPath
            return target
          }
        )
      })

      // update yarnInfo
      const yarnInfoParam = {
        skipNotification,
        yarnlogId,
        properGaugeList,
        isCopy,
        ...rest
      }

      externalLinksI18n.forEach(async ({ creating, locale, yarnLink }) => {
        let urlOperator = ''
        if (creating && yarnLink) {
          urlOperator = 'i'
        } else if (!creating && yarnLink) {
          urlOperator = 'u'
        } else if (!creating && !yarnLink) {
          urlOperator = 'd'
        } else {
          return
        }
        await usePost(`/yarn/link/${urlOperator}`, {
          skipNotification,
          yarnlogId,
          locale,
          yarnLink
        })
      })

      await Promise.all(bookNames.map(({ bookName, locale }) => {
        return operateTruthyList({
          maybe: bookName,
          target: bookName,
          callback: async bookName => {
            return await usePost('/yarn/book/i', {
              skipNotification,
              yarnlogId,
              bookName,
              locale
            })
          },
          list: [],
          operator: 'push'
        })
      }))

      await Promise.all(
        handlingMarks.map(handlingMarkId => {
          return usePost('/yarn/hand/i', {
            skipNotification,
            handlingMarkId,
            locale,
            yarnlogId
          })
        })
      )

      await Promise.all(
        certificationList.map((id) => {
          const itemOld = ecoOld.data.data.filter((item) => {
            return item.ecoMarkId === id.certificationId
          })
          let isUpdate = false
          let createTime = null
          let updateTime = null

          if (itemOld.length > 0) {
            createTime = itemOld[0].createTime
            if(itemOld[0].numLisence != id.numLicense
              || moment(itemOld[0].endEcoMark).format('yyyy-MM-dd') != moment(id.endPeriod).format('yyyy-MM-dd')
              || moment(itemOld[0].startEcoMark).format('yyyy-MM-dd') != moment(id.startPeriod).format('yyyy-MM-dd')){
                isUpdate = true
            } else{
              updateTime = itemOld[0].updateTime
            }
          }
          return usePost('/yarn/eco/i', {
            skipNotification,
            ecoMarkId: id.certificationId,
            numEcoMark: id.numLicense,
            startEcoMark:id.startPeriod,
            endEcoMark: id.endPeriod,
            locale,
            isUpdate : isUpdate,
            createTime: createTime,
            updateTime: updateTime,
            yarnlogId
          })
        })
      )
      let biggestSeq = materials.reduce((previous, current) => {
        if (current.seq > previous) {
          previous = current.seq
        }
        return previous
      }, 0)

      if (deletingMaterial) {
        await Promise.all(
          deletingMaterial.flatMap(({ materialId, seq }) =>
            materialId
              ? [
                  usePost('/yarn/material/i18n/d/a', { skipNotification, materialId, yarnlogId, seq }),
                  usePost('/yarn/material/d', { skipNotification, materialId, yarnlogId, seq })
                ]
              : []
          )
        )
      }

      await Promise.all(
        properGaugeList.map(({ gauge, gaugeEnds }, index) =>
          usePost('/yarn/pgauge/i', { skipNotification, yarnlogId, properGaugeId: gauge, ends: gaugeEnds, seq: index + 1 })
        )
      )

      const materialSeq = []
      await Promise.all(
        materials.map(({ materialId, materialOption, materialDetailOption, composition, seq }) => {
          if (seq === -1) {
            biggestSeq = biggestSeq + 1
            materialSeq.push(biggestSeq)
            return usePost('/yarn/material/i', {
              skipNotification,
              seq: biggestSeq,
              yarnlogId,
              materialId,
              materialOption,
              materialDetailOption,
              composition,
            })
          } else {
            materialSeq.push(seq)
            return usePost('/yarn/material/u', {
              skipNotification,
              seq,
              yarnlogId,
              materialId,
              materialOption,
              materialDetailOption,
              composition,
            })
          }
        })
      )

      await Promise.all(
        materials.flatMap(({ materialId, seq, i18nMaterials }, index) =>
          i18nMaterials.i18ns.map(({ new: isNew, displayName, locale }) => {
            if (isNew && displayName !== '') {
              return usePost('/yarn/material/i18n/i', { skipNotification, materialId, locale, yarnlogId, seq: materialSeq[index], displayName })
            } else if (displayName === '' && isNew !== true) {
              return usePost('/yarn/material/i18n/d', { skipNotification, seq: materialSeq[index], materialId, locale, yarnlogId })
            } else if (displayName !== null && isNew !== true) {
              return usePost('/yarn/material/i18n/u', { skipNotification, materialId, locale, yarnlogId, seq: materialSeq[index], displayName })
            }
          })
        )
      )

      await Promise.all(characteristicId.map(id => usePost('/yarn/char/i', { skipNotification, yarnlogId, charId: id })))
      await Promise.all(functionId.map(id => usePost('/yarn/func/i', { skipNotification, yarnlogId, functionId: id })))

      if (functionId.includes(FUNCTION_OTHER_ID)) {
        await Promise.all(
          funcI18ns.map(({ displayName, locale }) =>
            usePost('/yarn/func/i18n/i', { skipNotification, yarnlogId, functionId: FUNCTION_OTHER_ID, displayName, locale })
          )
        )
      }

      await Promise.all(
        freeComments.map(({ freeComment, locale }) => {
          return operateTruthyList({
            maybe: freeComment,
            target: freeComment,
            callback: async freeComment => {
              return await usePost('/yarn/comment/i', {
                skipNotification,
                freeComment,
                locale,
                yarnlogId
              })
            },
            list: [],
            operator: 'push'
          })
        })
      )
      await Promise.all(referDeletingImages.map(id => usePost('/yarn/refimg/d', { skipNotification, id })))
      await Promise.all(
        referenceImages.map(({ imgData, id }, index) => {
          if (imgData || insertRefImage) {
            return true
          }
          return usePost('/yarn/refimg/u', {
            defaultImage: index === referenceImagesDefault ? 'Y' : 'N',
            skipNotification,
            seq: index + 1,
            yarnlogId,
            yarnId: rest.yarnId,
            yarnNo: rest.yarnNo,
            id,
            companyId: rest.companyId,
            isCopy,
          })
        })
      )
      await Promise.all(
        referenceImages.map(({ imgData, imgPath, imgName }, index) => {
          if (!imgData && !insertRefImage) {
            return true
          }
          return usePost('/yarn/refimg/i', {
            defaultImage: index === referenceImagesDefault ? 'Y' : 'N',
            skipNotification,
            seq: index + 1,
            imgData,
            imgPath,
            imgName,
            yarnlogId,
            yarnId: rest.yarnId,
            yarnNo: rest.yarnNo,
            companyId: rest.companyId,
            isCopy,
          })
        })
      )

      await Promise.all(labels.map(labelId => usePost('/yarn/label/i', { skipNotification, labelId, yarnlogId })))
      await Promise.all(
        rest.regions.map(({ regionName, locale }) => {
           if (!regionName) {
            return true
           }
           usePost('/yarn/region/i', { skipNotification, yarnlogId, regionName, locale })
          }
        )
      )

      const result = await usePost('/yarn/u',
        omit(handleLogoData(yarnInfoParam, 'thumbnailData'), [
          'freeComment',
          'bookName'
        ]))
      return result
    })
  }

  const createLitePlanBook = async (params) => {
    const {
      labels,
      skipNotification,
      yarnId,
      yarnNo,
      companyId,
      typeYarn,
      season,
      yarnCount,
      priceZone,
      thumbnailData,
      thumbnailName,
      bookNames,
      handlingMarks,
      freeComments,
      characteristicId,
      functionId,
      externalLinksI18n,
      materials,
      properGaugeList,
      referenceImages,
      colorNum,
      funcI18ns,
      certificationList,
      referenceImagesDefault,
      regions,
      stock,
      moq,
      countryCode,
      isCopy
    } = params
  
    await usePost('/user/me')
    const { data: yarnData } = await usePost('/yarn/i', handleLogoData({
      skipNotification,
      yarnId,
      yarnNo,
      companyId,
      typeYarn,
      yarnCount,
      priceZone,
      season,
      thumbnailData,
      thumbnailName,
      colorNum,
      stock,
      moq,
      countryCode,
      isCopy,
      thumbnail: (isCopy && !thumbnailData) ? params.thumbnail : null 
    }, 'thumbnailData'))
  
    const yarnlogId = yarnData.data?.yarnlogId
    await Promise.all(bookNames.map(({ bookName, locale }) =>
      bookName ? usePost('/yarn/book/i', {
        skipNotification,
        yarnlogId,
        bookName,
        locale
      }) : null
    ))
  
    await Promise.all(labels.map(labelId =>
      usePost('/yarn/label/i', {
        skipNotification,
        labelId,
        yarnlogId
      })
    ))
    await Promise.all(externalLinksI18n.map(({ yarnLink, locale }) =>
      yarnLink ? usePost('/yarn/link/i', {
        skipNotification,
        yarnlogId,
        yarnLink,
        locale
      }) : null
    ))
  
    await Promise.all(handlingMarks.map(handlingMarkId =>
      usePost('/yarn/hand/i', {
        skipNotification,
        handlingMarkId,
        yarnlogId
      })
    ))
  
    await Promise.all(freeComments.map(({ freeComment, locale }) =>
      freeComment ? usePost('/yarn/comment/i', {
        skipNotification,
        freeComment,
        locale,
        yarnlogId
      }) : null
    ))
    const materialCalls = []
    materials.forEach(({ materialId, materialOption, materialDetailOption, composition, i18nMaterials }, index) => {
      const seq = index + 1
  
      materialCalls.push(usePost('/yarn/material/i', {
        skipNotification,
        seq,
        yarnlogId,
        materialId,
        materialOption,
        materialDetailOption,
        composition
      }))
  
      i18nMaterials.i18ns.forEach(({ displayName, locale }) => {
        if (displayName) {
          materialCalls.push(usePost('/yarn/material/i18n/i', {
            skipNotification,
            materialId,
            locale,
            yarnlogId,
            seq,
            displayName
          }))
        }
      })
    })
  
    await Promise.all(materialCalls)
  
    await Promise.all(characteristicId.map(id =>
      usePost('/yarn/char/i', {
        skipNotification,
        yarnlogId,
        charId: id
      })
    ))

    await Promise.all(certificationList.map(cert =>
      usePost('/yarn/eco/i', {
        skipNotification,
        yarnlogId,
        ecoMarkId: cert.certificationId,
        numEcoMark: cert.numLicense,
        startEcoMark: cert.startPeriod,
        endEcoMark: cert.endPeriod,
        isUpdate: false
      })
    ))
  
    await Promise.all(functionId.map(id =>
      usePost('/yarn/func/i', {
        skipNotification,
        yarnlogId,
        functionId: id
      })
    ))
  
    if (functionId.includes(FUNCTION_OTHER_ID)) {
      await Promise.all(funcI18ns.map(({ displayName, locale }) =>
        usePost('/yarn/func/i18n/i', {
          skipNotification,
          yarnlogId,
          functionId: FUNCTION_OTHER_ID,
          displayName,
          locale
        })
      ))
    }
  
    await Promise.all(properGaugeList.map((properGauge, index) => {
      const seq = index + 1
      return usePost('/yarn/pgauge/i', {
        skipNotification,
        yarnlogId,
        properGaugeId: properGauge.gauge,
        ends: properGauge.gaugeEnds,
        seq
      })
    }))
  
    await Promise.all(referenceImages.map(({ imgData, imgPath, imgName }, index) => {
      if (!imgData && !isCopy) return null
      return usePost('/yarn/refimg/i', {
        defaultImage: index === referenceImagesDefault ? 'Y' : 'N',
        skipNotification,
        seq: index + 1,
        imgData,
        imgPath,
        imgName,
        yarnlogId,
        yarnId,
        yarnNo,
        companyId,
        isCopy
      })
    }))

    await Promise.all(
      regions.map(({ regionName, locale }) => {
         if (!regionName) {
          return true
         }
         usePost('/yarn/region/i', { skipNotification, yarnlogId, regionName, locale })
        }
      )
    )
  }

  const updateYarnStatus = async ({ yarnId, rollback, ...rest }) => {
    if (!yarnId) {
      throw new Error('yarnlogId required')
    }
    const result = await usePost('/yarn/u/s', { yarnId, ...rest })
    return result.data.data
  }
  
  const cancelDownloadYarn = ({ yarnlogId, userId, isDownloaded }) => {
    return apiWrapper(async () => {
      usePost(`/download/i`, { yarnlogId, userId, isDownloaded });
      return true;
    });
  };

  const getAllRequiredValue = async ({ locale }) => {
    return apiWrapper(async () => {
      const result = await Promise.all([
        usePost('/code/q/a/', { mcdId: 101, locale }),
        usePost('/code/q/a/', { mcdId: 20, locale }),
        usePost('/code/q/a/', { mcdId: 108, locale }),
        usePost('/code/q/a/', { mcdId: 105, locale }),
        usePost('/code/q/a/', { mcdId: 107, locale }),
        usePost('/code/q/a/', { mcdId: 106, locale }),
        usePost('/code/q/a/', { mcdId: 110, locale }),
        usePost('/country/q/a', { countryName: '%', locale }),
        usePost('/code/q/a', { mcdId: 113, locale }),
      ])
      return result.map(({ data: { data } }) => {
        return data
      })
    })
  }
  
  const getYarnByLocale = async ({ companyId, publicYarn, locale }) => {
    const result = usePost(`/yarn/q/g/`, {
      companyId,
      locale,
    });
    if (result.data.status === 200) {
      list.value =  publicYarn
        ? res.data.data.filter(({ yarnStatus }) => {
            return yarnStatus === YARN_STATUS.PUBLIC;
          })
        : res.data.data;
    }
  };
  
  const viewYarn = ({ userId, locale, yarnlogId }) => {
    usePost('/viewed/i', { userId, locale, yarnlogId })
  }

  const getCopyDataForYarnForm = async ({ yarnlogId, companyId }) => {
    const {
      data: { data: yarn },
    } = await usePost('/yarn/q/cp', { yarnlogId, companyId, locale: locale.value })

    const [
      {
        data: { data: bookNames }
      },
      {
        data: { data: yarnpropergauges }
      },
      {
        data: { data: yarnMaterials }
      },
      {
        data: { data: handlingMarks }
      },
      {
        data: { data: yarncomments }
      },
      {
        data: { data: yarnColors }
      },
      {
        data: { data: referenceImages }
      },
      {
        data: { data: externalLinks }
      },
      {
        data: { data: funcI18ns }
      },
      {
        data: { data: ecos }
      },
      {
        data: { data: labels }
      },
      {
        data: { data: regions }
      }
    ] = await Promise.all([
      usePost('/yarn/book/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/pgauge/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/material/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/hand/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/comment/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/color/q', { yarnlogId, locale: locale.value }),
      usePost('/yarn/refimg/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/link/q/a', { yarnlogId }),
      usePost('/yarn/func/i18n/q/a', { yarnlogId, functionId: FUNCTION_OTHER_ID }),
      usePost('/yarn/eco/q/a', { yarnlogId }),
      usePost('/yarn/label/q/a', { yarnlogId, locale: locale.value }),
      usePost('/yarn/region/q/a', { yarnlogId, locale: locale.value })
    ])

    const externalLinksI18n = langCommon.languageOptions.value.reduce(
      (previous, { text, value }) => {
        doByCondition(
          externalLinks.find(({ locale }) => {
            return locale === value
          }),
          ({ locale, yarnLink }) => {
            previous.push({
              label: text,
              yarnlogId,
              locale,
              yarnLink
            })
          },
          () => {
            previous.push({
              label: text,
              locale: value,
              yarnLink: '',
              yarnlogId,
              creating: true
            })
          }
        )
        return previous
      },
      []
    )

    const i18nMaterials = await Promise.all(
      yarnMaterials.map(async ({ materialId, seq }) => {
        const {
          data: { data }
        } = await usePost('/yarn/material/i18n/q/a', {
          yarnlogId,
          materialId,
          seq
        })
  
        return {
          i18ns: langCommon.languageOptions.value.map(({ text, value }) => {
            const target = data.find(({ locale: materialLocale }) => {
              return materialLocale === value
            })
            return target
              ? {
                  seq,
                  locale: target.locale,
                  label: text,
                  displayName: target.displayName,
                  yarnlogId: target.yarnlogId,
                  materialId: target.materialId
                }
              : {
                  new: true,
                  locale: value,
                  label: text,
                  displayName: '',
                  yarnlogId,
                  materialId,
                  seq
                }
          }),
          materialId,
          seq
        }
      })
    )

    const materialwithI18n = yarnMaterials.map(
      ({ materialId, materialOption, materialDetailOption, seq, id, composition }) => {
        return {
          id,
          composition,
          materialId,
          materialOption,
          materialDetailOption,
          seq,
          i18nMaterials: i18nMaterials.find(
            ({ materialId: materialIdInI18n, seq: i18nSeq }) => {
              return materialIdInI18n === materialId && seq === i18nSeq
            }
          )
        }
      }
    )

    const referenceImagesDefault = referenceImages.findIndex(
      ({ defaultImage }) => {
        return defaultImage === 'Y'
      }
    )

    const colorDefaultIndex = yarnColors.findIndex(({ defaultImage }) => {
      return defaultImage === 'Y'
    })

    yarnFormCopy.value = {
      ...yarn,
      labels:
        labels && labels?.length
          ? labels.map(({ labelId }) => {
              return labelId
            })
          : [],
      referDeletingImages: [],
      externalLinksI18n,
      characteristicId:
        typeof yarn.characteristicId === 'string'
          ? yarn.characteristicId
              .split(',')
              .filter(n => n)
              .map(Number)
          : [],
      functionId:
        typeof yarn.functionId === 'string'
          ? yarn.functionId
              .split(',')
              .filter(n => n)
              .map(Number)
          : [],
      thumbnailName: null,
      thumbnailData: null,
      bookNames: langCommon.languageOptions.value.map(({ value, text }) => {
        const target = bookNames.find(({ locale }) => {
          return locale === value
        })
        return {
          locale: value,
          bookName: target ? target.bookName : '',
          label: text
        }
      }),
      properGaugeList: yarnpropergauges.map(yarnpropergauge => {
        // 값
        return {
          gauge: yarnpropergauge.properGaugeId,
          properGauge: yarnpropergauge.properGaugeName,
          gaugeEnds: yarnpropergauge.ends
        }
      }),
      materials:
        materialwithI18n.length === 0
          ? getMaterialInitValue(langCommon.languageOptions.value)
          : materialwithI18n,
      freeComments: langCommon.languageOptions.value.map(({ value, text }) => {
        const result = doByCondition(
          yarncomments.find(({ locale }) => {
            return locale === value
          }),
          ({ freeComment }) => {
            return {
              locale: value,
              label: text,
              freeComment
            }
          },
          () => {
            return { locale: value, freeComment: '', label: text }
          }
        )
        return result.true || result.false
      }),
      colorDefaultIndex: colorDefaultIndex === -1 ? null : colorDefaultIndex,
      colors: yarnColors.map(
        ({
          colorName,
          colorType,
          colorCategoryId,
          imgPath,
          id,
          seq,
          defaultImage
        }) => {
          const col = {
            colorName,
            colorType,
            imgPath,
            id,
            seq
          }
          col.colorData = null
          col.colorCategoryIds = colorCategoryId
            ? colorCategoryId.split(',').map(Number)
            : []
          return col
        }
      ),
      referenceImagesDefault:
        referenceImagesDefault === -1 ? null : referenceImagesDefault,
      referenceImages: referenceImages.map(referenceImage => {
        const el = { ...referenceImage }
        el.defaultImage = !el.defaultImage ? 'N' : el.defaultImage
        el.imgName = ''
        el.imgData = ''
        return el
      }),
      handlingMarks: handlingMarks.map(({ handlingMarkId }) => {
        return handlingMarkId
      }),
      certificationId: ecos.map(({ ecoMarkId }) => {
        return ecoMarkId
      }),
      numCertification: ecos.map(({ numLisence }) => {
        return numLisence
      }),
      startCertification: ecos.map(({ startEcoMark }) => {
        return startEcoMark
      }),
      endCertification: ecos.map(({ endEcoMark }) => {
        return endEcoMark
      }),
      funcI18ns: langCommon.languageOptions.value.map(({ text, value }) => {
        const target = funcI18ns.find(({ locale }) => {
          return locale === value
        })
        return target
          ? {
              locale: target.locale,
              label: text,
              displayName: target.displayName
            }
          : {
              locale: value,
              label: text,
              displayName: ''
            }
      }),
      regions: langCommon.languageOptions.value.map(({ text, value }) => {
        const target = regions.find(({ locale }) => {
          return locale === value
        })
        return target
          ? {
              yarnlogId,
              locale: target.locale,
              label: text,
              regionName: target.regionName,
            }
          : {
              yarnlogId,
              locale: value,
              label: text,
              regionName: '',
            }
      })
    }
  }

  return {
    list,
    listByCompany,
    listByCompanyCount,
    yarnStatus,
    yarnStatusMap,
    listClickStatus,
    yarnForm,
    colorTypeOptions,
    yarnlogId,
    handlingMarks,
    yarnDetail,
    banYarnCard,
    yarnNewArrivals,
    yarnPopulars,
    yarnFormColors,
    yarnColorLists,
    yarnRefImages,
    yarnColorLocalUrls,
    yarnFormCopy,
    get,
    getYarnStatus,
    getColorTypeOptions,
    resetStoreCache,
    getDataForYarnPrSupplier,
    yarnStatusForRadio,
    getYarnStatistic,
    getDatamallRefImages,
    getYarnDetailFromServer,
    getListByCompany,
    deleteYarn,
    updateYarnStatus,
    getYarnColorListsFromServer,
    downloadYarn,
    getYarnRefImagesFromServer,
    cancelDownloadYarn,
    handleMarks,
    changeYarnFormColorCode,
    changeYarnFormColorType,
    replaceYarnFormColorImage,
    addYarnColorLocalUrls,
    resetYarnColorLocalUrls,
    getDataForYarnForm,
    updateWholeYarn,
    createLitePlanBook,
    getAllRequiredValue,
    getYarnByLocale,
    viewYarn,
    getCopyDataForYarnForm
  }
})